import { PeriodFilter } from '@/components/ui/analytics/PeriodFilter';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/shadcn/chart';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/shadcn/tabs';
import { useChartHeightClass } from '@/hooks/use-chart-height';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { formatCurrency } from '@/lib/utils';
import { type BreadcrumbItem, type Lead, type LeadMetrics, type SourceConversion, type StatusOption, type UserPerformance } from '@/types';
import type { AnalyticsDateRange } from '@/types/analytics';
import { Head, usePage } from '@inertiajs/react';
import { Building2, Calendar, Clock, DollarSign, Target, TrendingUp, User as UserIcon, Users } from 'lucide-react';
import * as React from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, XAxis, YAxis } from 'recharts';

interface DashboardPageProps {
    metrics: LeadMetrics;
    statusCounts: Record<string, number>;
    totalValueByStatus: Record<string, number>;
    sourceCounts: Record<string, number>;
    sourceConversions: Record<string, SourceConversion>;
    userPerformance: UserPerformance[];
    agingLeads: Lead[];
    recentLeads: Lead[];
    statuses: StatusOption[];
    dateRange: AnalyticsDateRange;
    [key: string]: unknown;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

export default function LeadsDashboard() {
    const { props } = usePage<DashboardPageProps>();
    const {
        metrics,
        statusCounts,
        totalValueByStatus,
        sourceCounts,
        sourceConversions,
        userPerformance,
        agingLeads,
        recentLeads,
        statuses,
        dateRange,
    } = props;
    const { t } = useTranslations();
    const [isNavigating, setIsNavigating] = React.useState(false);
    const chartHeightClass = useChartHeightClass('md');

    const breadcrumbs: BreadcrumbItem[] = [
        { title: t('leads.dashboard.breadcrumbs.dashboard', 'Dashboard'), href: '/dashboard' },
        { title: t('leads.dashboard.breadcrumbs.leads', 'Leads'), href: '/leads' },
        { title: t('leads.dashboard.breadcrumbs.analytics', 'Analytics'), href: '/leads-dashboard' },
    ];

    // Prepare chart data
    const statusChartData = statuses.map((status) => ({
        name: status.label,
        count: statusCounts[status.value] || 0,
        value: totalValueByStatus[status.value] || 0,
        fill: status.color.includes('blue')
            ? '#3B82F6'
            : status.color.includes('yellow')
              ? '#F59E0B'
              : status.color.includes('green')
                ? '#10B981'
                : status.color.includes('purple')
                  ? '#8B5CF6'
                  : status.color.includes('emerald')
                    ? '#059669'
                    : '#EF4444',
    }));

    const sourceChartData = Object.entries(sourceCounts).map(([source, count]) => ({
        name: source.replace('_', ' ').toUpperCase(),
        count,
        conversion: sourceConversions[source]?.conversion_rate || 0,
    }));

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={t('leads.dashboard.title', 'Leads Dashboard')} />

            <div className="flex h-full flex-1 flex-col gap-6 p-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold">{t('leads.dashboard.heading', 'Lead Analytics')}</h1>
                        <p className="text-muted-foreground">{t('leads.dashboard.subheading', 'Comprehensive insights into your lead pipeline')}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <PeriodFilter dateRange={dateRange} onNavigatingChange={setIsNavigating} />
                        <Button variant="outline" size="sm">
                            <Calendar className="mr-2 h-4 w-4" />
                            {t('leads.dashboard.export_report', 'Export Report')}
                        </Button>
                    </div>
                </div>

                <div
                    className={
                        isNavigating
                            ? 'pointer-events-none flex flex-col gap-6 opacity-50 transition-opacity'
                            : 'flex flex-col gap-6 transition-opacity'
                    }
                >
                    {/* Key Metrics */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">{t('leads.dashboard.metrics.total_leads', 'Total Leads')}</CardTitle>
                                <Users className="text-muted-foreground h-4 w-4" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">{metrics.total_leads}</div>
                                <p className="text-muted-foreground text-xs">{t('leads.dashboard.metrics.total_leads_sub', 'Active pipeline')}</p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">{t('leads.dashboard.metrics.pipeline_value', 'Pipeline Value')}</CardTitle>
                                <DollarSign className="text-muted-foreground h-4 w-4" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">{formatCurrency(metrics.total_value ?? 0)}</div>
                                <p className="text-muted-foreground text-xs">
                                    {t('leads.dashboard.metrics.pipeline_value_sub', 'Total estimated value')}
                                </p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">{t('leads.dashboard.metrics.won_leads', 'Won Leads')}</CardTitle>
                                <Target className="text-muted-foreground h-4 w-4" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">{metrics.won_leads}</div>
                                <p className="text-muted-foreground text-xs">{t('leads.dashboard.metrics.won_leads_sub', 'Successfully closed')}</p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">
                                    {t('leads.dashboard.metrics.conversion_rate', 'Conversion Rate')}
                                </CardTitle>
                                <TrendingUp className="text-muted-foreground h-4 w-4" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">{metrics.conversion_rate}%</div>
                                <p className="text-muted-foreground text-xs">
                                    {t('leads.dashboard.metrics.conversion_rate_sub', 'Lead to customer')}
                                </p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Charts */}
                    <Tabs defaultValue="pipeline" className="space-y-4">
                        <TabsList>
                            <TabsTrigger value="pipeline">{t('leads.dashboard.tabs.pipeline', 'Pipeline Overview')}</TabsTrigger>
                            <TabsTrigger value="sources">{t('leads.dashboard.tabs.sources', 'Lead Sources')}</TabsTrigger>
                            <TabsTrigger value="performance">{t('leads.dashboard.tabs.performance', 'Team Performance')}</TabsTrigger>
                        </TabsList>

                        <TabsContent value="pipeline" className="space-y-4">
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>{t('leads.dashboard.charts.leads_by_status', 'Leads by Status')}</CardTitle>
                                        <CardDescription>
                                            {t('leads.dashboard.charts.leads_by_status_desc', 'Distribution of leads across pipeline stages')}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <ChartContainer
                                            role="img"
                                            aria-label={t('leads.dashboard.charts.leads_by_status', 'Leads by Status')}
                                            config={{
                                                count: {
                                                    label: t('leads.dashboard.charts.series_leads', 'Leads'),
                                                    color: 'hsl(var(--chart-1))',
                                                },
                                            }}
                                            className={chartHeightClass}
                                        >
                                            <BarChart data={statusChartData}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" />
                                                <YAxis />
                                                <ChartTooltip content={<ChartTooltipContent />} />
                                                <Bar dataKey="count" fill="var(--color-count)" />
                                            </BarChart>
                                        </ChartContainer>
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardHeader>
                                        <CardTitle>{t('leads.dashboard.charts.value_by_status', 'Value by Status')}</CardTitle>
                                        <CardDescription>
                                            {t('leads.dashboard.charts.value_by_status_desc', 'Total estimated value per pipeline stage')}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <ChartContainer
                                            role="img"
                                            aria-label={t('leads.dashboard.charts.value_by_status', 'Value by Status')}
                                            config={{
                                                value: {
                                                    label: t('leads.dashboard.charts.series_value', 'Value'),
                                                    color: 'hsl(var(--chart-2))',
                                                },
                                            }}
                                            className={chartHeightClass}
                                        >
                                            <BarChart data={statusChartData}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" />
                                                <YAxis />
                                                <ChartTooltip content={<ChartTooltipContent />} />
                                                <Bar dataKey="value" fill="var(--color-value)" />
                                            </BarChart>
                                        </ChartContainer>
                                    </CardContent>
                                </Card>
                            </div>
                        </TabsContent>

                        <TabsContent value="sources" className="space-y-4">
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>{t('leads.dashboard.charts.lead_sources', 'Lead Sources')}</CardTitle>
                                        <CardDescription>
                                            {t('leads.dashboard.charts.lead_sources_desc', 'Where your leads are coming from')}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <ChartContainer
                                            role="img"
                                            aria-label={t('leads.dashboard.charts.lead_sources', 'Lead Sources')}
                                            config={{
                                                count: {
                                                    label: t('leads.dashboard.charts.series_leads', 'Leads'),
                                                    color: 'hsl(var(--chart-1))',
                                                },
                                            }}
                                            className={chartHeightClass}
                                        >
                                            <PieChart>
                                                <Pie
                                                    data={sourceChartData}
                                                    cx="50%"
                                                    cy="50%"
                                                    labelLine={false}
                                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                    outerRadius={80}
                                                    fill="#8884d8"
                                                    dataKey="count"
                                                >
                                                    {sourceChartData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <ChartTooltip content={<ChartTooltipContent />} />
                                            </PieChart>
                                        </ChartContainer>
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardHeader>
                                        <CardTitle>{t('leads.dashboard.charts.source_conversion', 'Source Conversion Rates')}</CardTitle>
                                        <CardDescription>
                                            {t('leads.dashboard.charts.source_conversion_desc', 'Effectiveness of each lead source')}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <ChartContainer
                                            role="img"
                                            aria-label={t('leads.dashboard.charts.source_conversion', 'Source Conversion Rates')}
                                            config={{
                                                conversion: {
                                                    label: t('leads.dashboard.charts.series_conversion', 'Conversion %'),
                                                    color: 'hsl(var(--chart-3))',
                                                },
                                            }}
                                            className={chartHeightClass}
                                        >
                                            <BarChart data={sourceChartData}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" />
                                                <YAxis />
                                                <ChartTooltip content={<ChartTooltipContent />} />
                                                <Bar dataKey="conversion" fill="var(--color-conversion)" />
                                            </BarChart>
                                        </ChartContainer>
                                    </CardContent>
                                </Card>
                            </div>
                        </TabsContent>

                        <TabsContent value="performance" className="space-y-4">
                            <Card>
                                <CardHeader>
                                    <CardTitle>{t('leads.dashboard.team.title', 'Team Performance')}</CardTitle>
                                    <CardDescription>
                                        {t('leads.dashboard.team.desc', 'Individual team member lead conversion rates')}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        {Array.isArray(userPerformance) && userPerformance.length > 0 ? (
                                            userPerformance.map((user) => (
                                                <div key={user.id} className="flex items-center justify-between rounded-lg border p-4">
                                                    <div className="flex items-center gap-3">
                                                        <UserIcon className="text-muted-foreground h-8 w-8" />
                                                        <div>
                                                            <p className="font-medium">{user.name}</p>
                                                            <p className="text-muted-foreground text-sm">
                                                                {user.total_leads} {t('leads.dashboard.team.leads', 'leads')} • {user.converted_leads}{' '}
                                                                {t('leads.dashboard.team.converted', 'converted')}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <Badge variant={user.conversion_rate >= 20 ? 'default' : 'secondary'}>
                                                        {user.conversion_rate}% {t('leads.dashboard.team.conversion', 'conversion')}
                                                    </Badge>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-muted-foreground py-4 text-center text-sm">
                                                {t('leads.dashboard.team.empty', 'No user performance data available')}
                                            </p>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>

                    {/* Additional Insights */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Clock className="h-5 w-5" />
                                    {t('leads.dashboard.aging.title', 'Aging Leads')}
                                </CardTitle>
                                <CardDescription>{t('leads.dashboard.aging.desc', "Leads that haven't been updated in 30+ days")}</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {agingLeads.slice(0, 5).map((lead) => (
                                        <div key={lead.id} className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium">{lead.name}</p>
                                                <p className="text-muted-foreground text-sm">
                                                    {lead.company?.name || t('leads.dashboard.no_company', 'No company')}
                                                </p>
                                            </div>
                                            <Badge variant="outline">
                                                {lead.aging_days} {t('leads.dashboard.aging.days', 'days')}
                                            </Badge>
                                        </div>
                                    ))}
                                    {agingLeads.length === 0 && (
                                        <p className="text-muted-foreground text-sm">{t('leads.dashboard.aging.empty', 'No aging leads found')}</p>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Building2 className="h-5 w-5" />
                                    {t('leads.dashboard.recent.title', 'Recent Activity')}
                                </CardTitle>
                                <CardDescription>{t('leads.dashboard.recent.desc', 'Latest leads added to the pipeline')}</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {recentLeads.slice(0, 5).map((lead) => (
                                        <div key={lead.id} className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium">{lead.name}</p>
                                                <p className="text-muted-foreground text-sm">
                                                    {lead.company?.name || t('leads.dashboard.no_company', 'No company')}
                                                </p>
                                            </div>
                                            <Badge variant="outline">{lead.status}</Badge>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
}
